/**
 * 생성 조건(넓은 화면) — 문제 수·유형·난이도를 칩 3개로 나눠 막대에 둔다(10/23 지민). 칩은 「항목 아이콘 + 값」만
 * 보이고(이름은 툴팁), 누르면 그 항목의 선택지가 위로 바로 뜬다(2단 메뉴 없음).
 * 하나만 고르는 문제 수·난이도는 라디오, 여러 개 고르는 유형은 체크박스(줄 왼쪽).
 * 고르는 동안 메뉴는 닫히지 않는다 — 유형은 여러 개를 고르고, 값은 칩에서 바로 보인다.
 * 좁은 화면은 AiGenerateDockConditionsSheet(필터 아이콘 → 바텀시트)가 맡는다.
 */
import { useState } from 'react';
import { RadioGroup } from '@zep-us/zus';
import { Button } from '@zep-us/zus/button';
import { Dropdown } from '@zep-us/zus/dropdown';
import { Tooltip } from '@zep-us/zus/tooltip';
import { QUIZ_TYPE_ICON_MAP } from '../../../components/EditableQuizCard/QuizTypeSelector/QuizTypeIcons';
import { AI_GENERATE_DOCK_DIFFICULTIES, AI_GENERATE_DOCK_QUIZ_TYPES } from '../consts/aiGenerateDock.const';
import { type AiGenerateDockConditions, type AiGenerateDockCopy } from '../types/aiGenerateDock.types';
import { type AiGenerateDockCountState } from '../utils/aiGenerateDockCount';
import { type AiBuilderQuizTypeHint } from '../utils/types';
import { ConditionIcon, ConditionOptionItem, DifficultyIcon } from './AiGenerateDockConditionItems';
export type AiGenerateDockConditionsMenuProps = {
  copy: AiGenerateDockCopy;
  conditions: AiGenerateDockConditions;
  onConditionsChange: (conditions: AiGenerateDockConditions) => void;
  countState: Extract<AiGenerateDockCountState, {
    locked: false;
  }>;
  remainingSlots: number;
  /** 칩 메뉴 중 하나라도 열려 있는지 — Esc 로 입력창을 닫을지 정하는 데 쓴다. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  /** 한 줄 요약 — 좁은 화면 시트 버튼의 접근 이름에 쓴다(여기선 안 그린다). */
  summary: string;
  /** 처음 열어 둘 칩 — 스토리북에서 선택지를 바로 보이려고 쓴다. */
  defaultLevel?: ConditionLevel | null;
};
export type ConditionLevel = 'count' | 'type' | 'difficulty';
const MENU_OFFSET_PX = 2;
const toggleQuizType = (quizTypes: AiBuilderQuizTypeHint[], type: AiBuilderQuizTypeHint): AiBuilderQuizTypeHint[] => quizTypes.includes(type) ? quizTypes.filter(item => item !== type) : AI_GENERATE_DOCK_QUIZ_TYPES.filter(item => item === type || quizTypes.includes(item));
export const AiGenerateDockConditionsMenu = ({
  copy,
  conditions,
  onConditionsChange,
  countState,
  open,
  onOpenChange,
  disabled,
  defaultLevel = null
}: AiGenerateDockConditionsMenuProps) => {
  // 어느 칩이 열렸는지는 여기서, 열림 자체는 부모(open)가 정한다 — 입력창이 숨겨지면 부모가 open 을 꺼 모두 닫는다.
  const [openLevel, setOpenLevel] = useState<ConditionLevel | null>(defaultLevel ?? (open ? 'count' : null));
  const handleOpenChange = (level: ConditionLevel) => (next: boolean) => {
    setOpenLevel(next ? level : null);
    onOpenChange(next);
  };
  const levelLabel: Record<ConditionLevel, string> = {
    count: copy.countLabel,
    type: copy.quizTypeLabel,
    difficulty: copy.difficultyLabel
  };

  /** 칩에 보이는 값 — 문제 수는 실제로 만들 수, 유형은 고른 개수, 난이도는 이름(10/23 지민). */
  const chipValue: Record<ConditionLevel, string> = {
    count: String(countState.count),
    type: String(conditions.quizTypes.length),
    difficulty: copy.difficulties[conditions.difficulty]
  };
  const chipIcon = (level: ConditionLevel) => level === 'difficulty' ? <DifficultyIcon difficulty={conditions.difficulty} /> : <ConditionIcon kind={level} />;
  const levelBody = (current: ConditionLevel) => {
    switch (current) {
      case 'count':
        return (
          // 고른 값을 그대로 켠다 — 남은 칸보다 커도 고를 수 있고, 보낼 수는 요약 칩이 보여 준다.
          <RadioGroup value={String(conditions.count)} className="gap-0" tabIndex={-1}>
            {countState.options.map(option => <ConditionOptionItem key={option.value} kind="radio" value={String(option.value)} testId={`ai-generate-dock-count-${option.value}`} selected={conditions.count === option.value} disabled={disabled} label={String(option.value)} onSelect={() => onConditionsChange({
              ...conditions,
              count: option.value
            })} />)}
          </RadioGroup>
        );
      case 'type':
        return AI_GENERATE_DOCK_QUIZ_TYPES.map(type => {
          const selected = conditions.quizTypes.includes(type);
          const isLastSelected = selected && conditions.quizTypes.length === 1;
          return <ConditionOptionItem key={type} kind="checkbox" value={type} testId={`ai-generate-dock-type-${type}`} selected={selected} disabled={disabled || isLastSelected} label={copy.quizTypes[type]} icon={QUIZ_TYPE_ICON_MAP[type]} onSelect={() => onConditionsChange({
            ...conditions,
            quizTypes: toggleQuizType(conditions.quizTypes, type)
          })} />;
        });
      case 'difficulty':
        return <RadioGroup value={String(conditions.difficulty)} className="gap-0" tabIndex={-1}>
            {AI_GENERATE_DOCK_DIFFICULTIES.map(difficulty => <ConditionOptionItem key={difficulty} kind="radio" value={String(difficulty)} testId={`ai-generate-dock-difficulty-${difficulty}`} selected={conditions.difficulty === difficulty} disabled={disabled} label={copy.difficulties[difficulty]} onSelect={() => onConditionsChange({
            ...conditions,
            difficulty
          })} />)}
          </RadioGroup>;
    }
  };
  const levels: ConditionLevel[] = ['count', 'type', 'difficulty'];
  return <Tooltip.Provider data-sentry-element="unknown" data-sentry-component="AiGenerateDockConditionsMenu" data-sentry-source-file="AiGenerateDockConditionsMenu.tsx">
      <div className="flex min-w-0 items-center gap-4xs">
        {levels.map(level => <Dropdown.Root key={level} open={open && openLevel === level} onOpenChange={handleOpenChange(level)}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <Dropdown.Trigger asChild>
                  <Button size="sm" variant="transparent" intent="neutral" disabled={disabled} aria-label={`${levelLabel[level]}: ${chipValue[level]}`} data-testid={`ai-generate-dock-conditions-${level}`}
              // 참고 화면처럼 판 없이 「아이콘 + 값」만 — ZUS transparent/neutral(hover 막만 뜬다).
              // 칩끼리 붙어 보이게 좌우 여백 12 → 6, 칩 사이 간격 2(10/23 지민). ZUS 기본 px-[12px] 는 ! 로 덮는다.
              className="shrink-0 !px-2xs text-body-sm-semibold text-neutral" leftSlot={chipIcon(level)}>
                    {chipValue[level]}
                  </Button>
                </Dropdown.Trigger>
              </Tooltip.Trigger>
              {!(open && openLevel === level) && <Tooltip.Content side="top" sideOffset={2} arrow>
                  {levelLabel[level]}
                </Tooltip.Content>}
            </Tooltip.Root>
            <Dropdown.Portal>
              <Dropdown.Content side="top" align="start" sideOffset={MENU_OFFSET_PX} data-testid={`ai-generate-dock-conditions-menu-${level}`} className="max-h-[320px] w-[200px] max-w-[calc(100vw-32px)] overflow-y-auto px-xs py-xs">
                {/* Figma MenuLabel — 항목 이름(caption/lg semibold 회색). 메뉴 항목이 아니라 머리 줄이다. */}
                <p className="flex min-h-xl items-center px-2xs text-caption-lg-semibold text-alternative">
                  {levelLabel[level]}
                </p>
                {levelBody(level)}
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>)}
      </div>
    </Tooltip.Provider>;
};